import React, { useEffect, useRef, useState } from "react";
import { useTheme } from "../context/ThemeContext";

// Playful custom cursor with switchable styles:
//  - "bubble": soft ring + dot with a rising bubble trail
//  - "fin":    a little shark fin that tilts toward the movement direction
//  - "normal": no custom cursor at all (uses the default system pointer)
// Disabled on touch devices for a clean mobile experience.
export default function BubbleCursor() {
  const { cursorStyle, dark } = useTheme();
  const ringRef = useRef(null);
  const dotRef = useRef(null);
  const finRef = useRef(null);
  const [bubbles, setBubbles] = useState([]);
  const [enabled, setEnabled] = useState(false);
  const idRef = useRef(0);
  const lastSpawn = useRef(0);
  const pos = useRef({ x: -100, y: -100 });
  const ring = useRef({ x: -100, y: -100 });
  const angle = useRef(0);
  const pressed = useRef(false);

  const isNormal = cursorStyle === "normal";

  useEffect(() => {
    const isTouch =
      typeof window !== "undefined" &&
      (window.matchMedia("(pointer: coarse)").matches || "ontouchstart" in window);
    if (isTouch || isNormal) {
      setEnabled(false);
      document.body.classList.remove("has-bubble-cursor");
      return;
    }
    setEnabled(true);
    document.body.classList.add("has-bubble-cursor");

    const onMove = (e) => {
      const prev = pos.current;
      pos.current = { x: e.clientX, y: e.clientY };
      const dx = pos.current.x - prev.x;
      const dy = pos.current.y - prev.y;
      if (Math.abs(dx) + Math.abs(dy) > 2) {
        angle.current = Math.atan2(dy, dx) * (180 / Math.PI);
      }
      if (dotRef.current) {
        dotRef.current.style.transform = `translate(${e.clientX}px, ${e.clientY}px)`;
      }
      const now = Date.now();
      if (now - lastSpawn.current > 90) {
        lastSpawn.current = now;
        const id = idRef.current++;
        const size = 6 + Math.random() * 12;
        const drift = (Math.random() - 0.5) * 40;
        setBubbles((prev2) => [
          ...prev2.slice(-16),
          { id, x: e.clientX, y: e.clientY, size, drift },
        ]);
        setTimeout(() => setBubbles((p) => p.filter((b) => b.id !== id)), 1100);
      }
    };

    const onDown = () => (pressed.current = true);
    const onUp = () => (pressed.current = false);
    const onHover = (e) => {
      const interactive = e.target.closest('a, button, [role="button"], input, textarea, select');
      if (ringRef.current) ringRef.current.classList.toggle("cursor-ring--active", !!interactive);
      if (finRef.current) finRef.current.classList.toggle("cursor-fin--active", !!interactive);
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mousemove", onHover);
    window.addEventListener("mousedown", onDown);
    window.addEventListener("mouseup", onUp);

    let raf;
    const loop = () => {
      ring.current.x += (pos.current.x - ring.current.x) * 0.18;
      ring.current.y += (pos.current.y - ring.current.y) * 0.18;
      if (ringRef.current) {
        const scale = pressed.current ? 0.7 : 1;
        ringRef.current.style.transform = `translate(${ring.current.x}px, ${ring.current.y}px) scale(${scale})`;
      }
      if (finRef.current) {
        const scale = pressed.current ? 0.8 : 1;
        finRef.current.style.transform = `translate(${ring.current.x}px, ${ring.current.y}px) rotate(${angle.current}deg) scale(${scale})`;
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mousemove", onHover);
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("mouseup", onUp);
      cancelAnimationFrame(raf);
      document.body.classList.remove("has-bubble-cursor");
    };
  }, [isNormal]);

  if (!enabled || isNormal) return null;
  const isFin = cursorStyle === "fin";

  return (
    <div className="pointer-events-none fixed inset-0 z-[9999]" aria-hidden="true">
      {bubbles.map((b) => (
        <span
          key={b.id}
          className="cursor-bubble"
          style={{ left: b.x, top: b.y, width: b.size, height: b.size, "--drift": `${b.drift}px` }}
        />
      ))}

      {/* Bubble style */}
      <div ref={ringRef} className="cursor-ring" style={{ opacity: isFin ? 0 : 1 }} />
      <div ref={dotRef} className="cursor-dot" style={{ opacity: isFin ? 0 : 1 }} />

      {/* Fin style */}
      <div ref={finRef} className="cursor-fin" style={{ opacity: isFin ? 1 : 0 }}>
        <svg width="30" height="30" viewBox="0 0 30 30">
          <path
            d="M4 24 C10 22 18 16 26 5 C24 15 22 22 26 24 C18 25 10 25 4 24 Z"
            fill={dark ? "#22d3ee" : "#0d9488"}
            stroke="#ffffff"
            strokeWidth="1.6"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    </div>
  );
}
